
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SignInForm, SignUpForm } from '@/components/auth/AuthForms';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Helmet } from 'react-helmet-async';

const Auth = () => {
  const { isAuthenticated, user, profile, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('signin');

  // Check URL params for initial tab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'signup') {
      setActiveTab('signup');
    }
  }, [searchParams]);

  // Role-based redirection
  useEffect(() => {
    if (isAuthenticated && user && profile && !isLoading) {
      console.log('Auth redirect triggered for:', user.email, 'role:', profile.role);
      
      // Route based on user role
      if (profile.role === "admin") {
        navigate("/admin", { replace: true });
      } else if (profile.role === "psychologist") {
        navigate("/psychologist-dashboard", { replace: true });
      } else {
        navigate("/patient-dashboard", { replace: true });
      }
    }
  }, [isAuthenticated, user, profile, navigate, isLoading]);

  const handleAuthSuccess = (role?: string) => {
    // Navigate immediately based on role if provided
    if (role) {
      console.log('🛡️ Auth success, navigating based on role:', role);
      if (role === "admin") {
        navigate("/admin", { replace: true });
      } else if (role === "psychologist") {
        navigate("/psychologist-dashboard", { replace: true });
      } else {
        navigate("/patient-dashboard", { replace: true });
      }
    }
  };

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[80vh] py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  // Don't show auth form if already authenticated
  if (isAuthenticated && user) {
    return (
      <div className="flex justify-center items-center min-h-[80vh] py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Redirecionando...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Autenticação | PsyOnline</title>
        <meta name="description" content="Faça login ou cadastre-se na plataforma PsyOnline" />
      </Helmet>

      <div className="flex justify-center items-center min-h-[80vh] py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-md">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signin">Entrar</TabsTrigger>
            <TabsTrigger value="signup">Cadastrar</TabsTrigger>
          </TabsList>
          
          <TabsContent value="signin">
            <SignInForm onSuccess={handleAuthSuccess} />
          </TabsContent>
          
          <TabsContent value="signup">
            <SignUpForm onSuccess={handleAuthSuccess} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Auth;
